import api from '@utils/api'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { notification } from 'antd'
import dayjs from 'dayjs'
import moment from 'moment'

import { type DriverInfoFormData, InCabTripModes, SupplyOptionModes } from './types'

export const IN_CAB_TRIP_MODE_OPTIONS = [
  {
    value: 'View All',
    title: 'View All',
    description: 'Driver can view all trips on their shift',
  },
  {
    value: 'View All (Restricted)',
    title: 'View All (Restricted)',
    description: 'Driver can view all stops but not the load or drop information',
  },
  {
    value: 'View Current',
    title: 'View Current',
    description: 'Driver can only view current trip',
  },
  {
    value: 'View All + Next Shift',
    title: 'View All + Next Shift',
    description: 'Driver can view all trips on their shift and the next shift, after completing post trip',
  },
]

export const IN_CAB_SUPPLY_OPTIONS_MODE = [
  {
    value: 'disabled',
    title: 'Disabled',
    description: 'System provides next best supply if marked unavailable',
  },
  {
    value: 'order_start',
    title: 'Order Start',
    description: 'Driver can select from supply options before "Begin Route"',
  },
  {
    value: 'terminal',
    title: 'Terminal',
    description: 'Driver can select supply options at the same terminal in Load Wizard',
  },
  {
    value: 'terminal_and_order_start',
    title: 'Terminal and Order Start',
    description: 'Both',
  },
]

export const addUpdateSaveValues = (formData: DriverInfoFormData, driver: Gravitate.Driver) => {
  const updatedDriver = { ...driver, ...formData }
  if (driver.shift_length.shift_length_hours !== formData.shift_length_hours) {
    updatedDriver.shift_length.shift_length_hours = formData.shift_length_hours
    updatedDriver.shift_length.type = 'driver'
  }
  updatedDriver.status = formData.active ? 'ACTIVE' : 'INACTIVE'
  updatedDriver.in_cab_supply_option_mode = formData.in_cab_supply_option_mode
  return updatedDriver
}
export const addCreateSaveValues = (formData: DriverInfoFormData) => {
  const updatedDriver = { ...formData }
  updatedDriver.active = true
  updatedDriver.status = 'ACTIVE'
  updatedDriver.username = formData.username || formData.name.toLowerCase().replace(' ', '')
  updatedDriver.in_cab_supply_option_mode = formData.in_cab_supply_option_mode

  return updatedDriver
}
export const saveDriver = async (
  driver,
  updatedDriver,
  setDriver,
  needsScheduleGenerated,
  resetFormState,
  upsertUpdatedDriver,
  useUpsertDriverMutation
) => {
  try {
    const resp = await useUpsertDriverMutation.mutateAsync({
      body: { ...updatedDriver, pattern_schedule_start_date: dayjs().utc().startOf('day').toISOString() },
    })
    if (!driver) {
      upsertUpdatedDriver(resp)
    } else {
      setDriver(updatedDriver)
      if (needsScheduleGenerated) {
        api.autoGenerateSchedules(updatedDriver.id)
      }
    }
    resetFormState()
  } catch (err: any) {
    if (err.detail) {
      notification.error({
        message: 'Update Failed',
        description: err.detail,
        placement: 'bottomRight',
      })
    }
    resetFormState(true)
  }
}
export const checkIfNeedsScheduleGeneration = (
  formData: DriverInfoFormData,
  driver: Gravitate.Driver,
  creatingDriver: boolean
) => {
  if (creatingDriver || formData.auto_schedule === false || formData.active === false) {
    return false
  }
  const preferenceChanged = formData.shift_preference !== driver.shift_preference
  const templateChanged = formData.preferred_template !== driver.preferred_template
  const amStartChanged =
    driver.am_shift_start_time &&
    formatters.DATE_TIME(formData.shift_start_time) !== formatters.DATE_TIME(moment(driver.am_shift_start_time))
  const pmStartChanged =
    driver.pm_shift_start_time &&
    formatters.DATE_TIME(formData.shift_start_time) !== formatters.DATE_TIME(moment(driver.pm_shift_start_time))
  const shiftLengthChanged = formData.shift_length_hours !== driver.shift_length.shift_length_hours
  return preferenceChanged || templateChanged || amStartChanged || pmStartChanged || shiftLengthChanged
}

export function getTripModeText(tripMode: (typeof InCabTripModes)[keyof typeof InCabTripModes]) {
  switch (tripMode) {
    case InCabTripModes.restricted:
      return 'View All (Restricted)'
    case InCabTripModes.current:
      return 'View Current'
    case InCabTripModes.allAndNext:
      return 'View All + Next Shift'
    default:
      return 'View All'
  }
}

export function getSupplyOptionsModeText(
  supplyOptionsMode: (typeof SupplyOptionModes)[keyof typeof SupplyOptionModes]
) {
  switch (supplyOptionsMode) {
    case SupplyOptionModes.terminal:
      return 'Terminal'
    case SupplyOptionModes.orderStart:
      return 'Order Start'
    case SupplyOptionModes.terminalAndOrderStart:
      return 'Terminal and Order Start'
    default:
      return 'Disabled'
  }
}
